/**
 * Utilities for playing audio from presigned S3 URLs.
 */

/** Wire common lifecycle handlers onto a media element. */
export const wireMediaElement = (
  media: HTMLMediaElement,
  opts: { preload?:  "metadata" | "auto" ; playbackRate?: number },
) => {
  media.preload = opts.preload ?? "auto";
  if (opts.playbackRate) media.playbackRate = opts.playbackRate;
};

/** Returns a promise that resolves when the element can play, or rejects on error. */
export const waitForPlayable = (media: HTMLMediaElement): Promise<void> =>
  new Promise((resolve, reject) => {
    media.oncanplaythrough = () => {
      media.play().then(() => resolve()).catch(reject);
    };
    media.onerror = () => {
      const code = media.error?.code ?? 0;
      const msg = media.error?.message ?? "";
      reject(new Error(`code=${code} ${msg}`));
    };
  });

/** Attempt 1: Direct URL + Audio element. */
export const playDirectUrl = async (
  url: string,
  playbackRate: number,
): Promise<HTMLMediaElement> => {
  const a = new Audio();
  wireMediaElement(a, { playbackRate });
  a.src = url;
  await waitForPlayable(a);
  return a;
};

/** Attempt 2: Fetch as blob + Audio element with audio/mp4 type. */
export const playBlobAudio = async (
  url: string,
  playbackRate: number,
): Promise<HTMLMediaElement> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  const raw = await res.blob();
  if (raw.size === 0) throw new Error("empty response");
  const blob = new Blob([raw], { type: "audio/mp4" });
  const blobUrl = URL.createObjectURL(blob);
  const a = new Audio();
  wireMediaElement(a, { playbackRate });
  a.src = blobUrl;
  await waitForPlayable(a);
  return a;
};

/** Attempt 3: Fetch as blob + Video element. */
export const playBlobVideo = async (
  url: string,
  playbackRate: number,
): Promise<HTMLMediaElement> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  const blob = await res.blob();
  if (blob.size === 0) throw new Error("empty response");
  const blobUrl = URL.createObjectURL(blob);
  const v = document.createElement("video");
  wireMediaElement(v, { playbackRate });
  v.src = blobUrl;
  await waitForPlayable(v);
  return v;
};

/** Attempt 4: Fetch + AudioContext decode + WAV re-encode. */
export const playAsWav = async (
  url: string,
  playbackRate: number,
): Promise<HTMLMediaElement> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  const arrayBuf = await res.arrayBuffer();
  if (arrayBuf.byteLength === 0) throw new Error("empty response");

  const ctx = new AudioContext();
  const audioBuf = await ctx.decodeAudioData(arrayBuf);
  const wavBlob = encodeWav(audioBuf);
  await ctx.close();

  const blobUrl = URL.createObjectURL(wavBlob);
  const a = new Audio();
  wireMediaElement(a, { playbackRate });
  a.src = blobUrl;
  await waitForPlayable(a);
  return a;
};

/** Encode an AudioBuffer as a WAV Blob. */
function encodeWav(audioBuf: AudioBuffer): Blob {
  const numCh = audioBuf.numberOfChannels;
  const sampleRate = audioBuf.sampleRate;
  const length = audioBuf.length;
  const dataSize = length * numCh * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numCh * 2, true);
  view.setUint16(32, numCh * 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      const sample = Math.max(-1, Math.min(1, audioBuf.getChannelData(ch)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: "audio/wav" });
}

/**
 * Try each playback strategy in order. Returns the first element that works.
 * Throws with a summary if all fail.
 */
export const playUrlWithFallbacks = async (
  url: string,
  playbackRate: number,
): Promise<HTMLMediaElement> => {
  const attempts = [
    { name: "direct", fn: () => playDirectUrl(url, playbackRate) },
    { name: "blob+audio", fn: () => playBlobAudio(url, playbackRate) },
    { name: "blob+video", fn: () => playBlobVideo(url, playbackRate) },
    { name: "wav", fn: () => playAsWav(url, playbackRate) },
  ];

  const errors: string[] = [];
  for (const { name, fn } of attempts) {
    try {
      return await fn();
    } catch (e: any) {
      errors.push(`${name}: ${e?.message ?? "?"}`);
    }
  }
  throw new Error(errors.join(" | "));
};
